import {
  DeviceResponseReportWrapper,
  ValueWrapper,
} from "src/repositories/api/dto/DeviceResponseReportWrapper";
import { globalAxiosAction } from "src/repositories/api/Init.api";
import {
  TimeWindowGroupCreateInDto,
  TimeWindowGroupPatchInDto,
} from "src/repositories/api/time-window-group/dto/Dto";
import { createDefaultAxios } from "src/repositories/api/Utils/AxiosUtility";
import {
  GeneralApiResponse,
  handleGeneralApiError,
} from "src/repositories/api/Utils/ResponseUtility";
import { getBrearerTokenHeader } from "src/repositories/api/Utils/Utils.api";
import {
  createFailedResponse,
  createSuccessResponse,
  FailedResponse,
} from "src/repositories/model/FailableResponse";
import { isNumber } from "src/utils/Utils";

const axiosInstance = createDefaultAxios("/api/timeWindowGroups");
globalAxiosAction.subscribe(axiosInstance);

async function get(
  groupId: number,
  top?: number,
  skip?: number,
  nameFilter?: string
): Promise<GeneralApiResponse<TimeWindowGroup[]>> {
  try {
    const response = await axiosInstance.get<ValueWrapper<TimeWindowGroup[]>>(
      "",
      {
        params: {
          $filter: `groupId eq ${groupId}${
            nameFilter != null ? ` and contains(Name,'${nameFilter}')` : ""
          }`,
          $top: top,
          $skip: skip,
        },
        ...getBrearerTokenHeader(),
      }
    );
    return createSuccessResponse(response.data.value);
  } catch (e) {
    return handleGeneralApiError(e);
  }
}

async function getCount(
  groupId: number,
  nameFilter?: string
): Promise<GeneralApiResponse<number>> {
  try {
    const response = await axiosInstance.get<number>("", {
      params: {
        $filter: `groupId eq ${groupId}${
          nameFilter != null ? ` and contains(Name,'${nameFilter}')` : ""
        }`,
      },
      ...getBrearerTokenHeader(),
    });
    return createSuccessResponse(response.data);
  } catch (e) {
    return handleGeneralApiError(e);
  }
}

async function delete_(
  id: number,
  deviceSns?: string[]
): Promise<GeneralApiResponse<DeviceResponseReportWrapper>> {
  try {
    const response = await axiosInstance.delete<DeviceResponseReportWrapper>(`${id}`, {
      params: {
        deviceSns,
      },
      ...getBrearerTokenHeader(),
    });

    return createSuccessResponse(response.data);
  } catch (e) {
    return handleGeneralApiError(e);
  }
}

async function create(
  dto: TimeWindowGroupCreateInDto,
  deviceSns?: string[]
): Promise<
  | GeneralApiResponse<DeviceResponseReportWrapper<TimeWindowGroup>>
  | FailedResponse<"idOnDevice-conflict", number>
> {
  try {
    const response = await axiosInstance.post<
      DeviceResponseReportWrapper<TimeWindowGroup>
    >("", dto, {
      params: {
        deviceSns,
      },
      ...getBrearerTokenHeader(),
    });
    return createSuccessResponse(response.data);
  } catch (e) {
    return handleGeneralApiError(e, (error, status, data) => {
      const conflictedId = data.conflictedIds?.[0];
      if (status == 409 && isNumber(conflictedId) == true) {
        return createFailedResponse("idOnDevice-conflict", conflictedId);
      }
      return null;
    });
  }
}

async function patch(
  id: number,
  dto: TimeWindowGroupPatchInDto,
  deviceSns?: string[]
): Promise<
  | GeneralApiResponse<DeviceResponseReportWrapper>
  | FailedResponse<"idOnDevice-conflict", number>
> {
  try {
    const response = await axiosInstance.patch<DeviceResponseReportWrapper>(
      `/${id}`,
      dto,
      {
        params: {
          deviceSns,
        },
        ...getBrearerTokenHeader(),
      }
    );
    return createSuccessResponse(response.data);
  } catch (e) {
    return handleGeneralApiError(e, (error, status, data) => {
      const conflictedId = data.conflictedIds?.[0];
      if (status == 409 && isNumber(conflictedId)) {
        return createFailedResponse("idOnDevice-conflict", conflictedId);
      }
      return null;
    });
  }
}

export const timeWindowGroupsApi = {
  get,
  getCount,
  create,
  patch,
  delete_,
};
